import { path } from "ramda";
import { getOrderDetails, verifyOrder, sendVerificationCode } from "./api";
import {
  addVerificationAttempt,
  isLoading,
  notLoading,
  setExpired,
  setLastAttempt,
  setOrderId,
  setOrderUUIDAndType,
  setPending,
  setStatus,
  setPhoneNumbers,
  flowNext,
  setPhoneType,
  setSubmitAttempts
} from "./action";
import { OrderStatus } from "./common";
import { getBuyOrders, getBuyOrder } from "@actions/buyOrders";
import {
  getOrderSell,
  getSellOrder,
  getSellOrders
} from "../../actions/sellOrderInfo";
import { sendBugsnagError } from "@utils/Bugsnag";

import { fireOneOff } from "../Emission/action";
import { logging } from "../Emission/logging";
import { setMessage } from "../Messages/action";

const responseErrorMessage = (error, placeholder) =>
  path("response", "data", "message", 0, error) || placeholder;

const isSchemaError = str =>
  str.indexOf("instance") === 0 &&
  ((str.indexOf("<") > -1 && str.indexOf(">") > -1) ||
    str.indexOf("subschema") > -1 ||
    str.indexOf("property") > -1);

const updateValidationStatus = status => dispatch => {
  switch (status) {
    case OrderStatus.expired:
      dispatch(setExpired());
      break;
    case OrderStatus.pending:
      dispatch(setPending());
      break;
    case OrderStatus.complete:
      dispatch(setStatus(OrderStatus.complete));
      break;
    default:
      throw Error(`Unsupported validation status ${status}.`);
  }
};

export const fetchOrderDetails =
  ({ token, orderId, type }) =>
  async (dispatch, getState, api) => {
    try {
      dispatch(isLoading());
      if (!token && !orderId)
        throw Error("You need to pass in the order token or the order UUID.");

      const {
        orderId: returnedOrderId,
        validationStatus,
        verifiableNumbers,
        orderType,
        orderUUID
      } = await getOrderDetails(api, { token, orderId, type });
      orderId = returnedOrderId;
      dispatch(setOrderUUIDAndType({ orderType, orderUUID }));
      let header = "";
      let body = "";
      if (validationStatus === "complete") {
        header = "Complete";
        body =
          "Your order is already verified. There is nothing left for you to do.";
      } else if (validationStatus === "expired") {
        if (orderType === "sell") {
          header = "Complete";
          body =
            "The verification process for this order has either been completed or has expired. Thank you.";
        } else {
          header = "Expired";
          body =
            "The verification process for this order has expired, and your order was cancelled. Please contact customer service.";
        }
      } else {
        dispatch(setPhoneNumbers(verifiableNumbers));
      }

      dispatch(setMessage(header, body));
      dispatch(updateValidationStatus(validationStatus));
      dispatch(setOrderId(orderId));
    } catch (error) {
      sendBugsnagError(
        error,
        "Modules/OrderVerification/thunk/fetchOrderDetails"
      );
      dispatch(
        setMessage("Could Not Find Order", "Please contact customer service.")
      );
    } finally {
      dispatch(notLoading());
    }
  };

export const sendVerification =
  ({ type, validationType, selectedPhone, orderUUID }) =>
  async (dispatch, getState, api) => {
    try {
      dispatch(setPhoneType(validationType));
      const { orderId, phoneValidationToken } = getState().orderVerification;

      const payload = {
        validationType,
        phoneNumberId: selectedPhone.id,
        phoneValidationToken
      };
      dispatch(isLoading());

      const response = await verifyOrder(api, {
        type,
        token: phoneValidationToken,
        orderId,
        orderUUID,
        payload
      });

      // on success
      dispatch(
        setLastAttempt(validationType, selectedPhone.id, phoneValidationToken)
      );
      dispatch(addVerificationAttempt({ payload, response }));
      dispatch(
        fireOneOff("PV_CODE_INITIATED", {
          COMMUNICATION_METHOD: validationType,
          SIDE: type
        })
      );
      dispatch(flowNext());
      return "success";
    } catch (error) {
      // NOTE: We set last attempt method to null. some
      sendBugsnagError(
        error,
        "Modules/OrderVerification/thunk/sendVerification"
      );
      const defaultMessage =
        "Failed to send verification code. Please contact customer service.";
      let message = responseErrorMessage(error, defaultMessage);

      if (isSchemaError(message)) message = defaultMessage;

      dispatch(setMessage("Failed To Verify", message));
      return message;
    } finally {
      dispatch(notLoading());
    }
  };

/**
 * @param {import("./api").VerificationPayload}
 */
export const submitVerificationCode =
  ({ code, type, orderId, refreshOrders = true }) =>
  async (dispatch, getState, api) => {
    dispatch(isLoading());
    dispatch(
      setSubmitAttempts(getState().orderVerification.submitAttempts + 1)
    );
    const error = await sendVerificationCode(
      api,
      {
        code,
        orderId,
        type
      },
      getState().initAppReducer.signedIn,
      dispatch
    );

    // NOTE: This was added because the SellOrders page.
    //       Further more, I'm unsure if this is the best solution going forward, or
    //       these two dispatch statements should be passed in as flags.
    if (refreshOrders) {
      if (type === "sell") {
        dispatch(getSellOrders(true));
        dispatch(getSellOrder(orderId));
        dispatch(getOrderSell(orderId));
      } else {
        dispatch(getBuyOrders());
        dispatch(getBuyOrder(orderId));
      }
    }
    dispatch(notLoading());

    const method = path(
      ["customerInfo", "verificationAttempts", orderId, "method"],
      getState()
    );

    if (error) {
      dispatch(
        fireOneOff("PV_COMPLETE", {
          COMMUNICATION_METHOD: method,
          STATUS: "FAILURE",
          SIDE: type
        })
      );
      return error;
    } else {
      dispatch(flowNext());
      dispatch(
        fireOneOff("PV_COMPLETE", {
          COMMUNICATION_METHOD: method,
          STATUS: "SUCCESS",
          SIDE: type
        })
      );
      return null;
    }
  };

export const resend = (type, orderUUID) => async (dispatch, getState, api) => {
  try {
    const { validationType, phoneUUID, validationToken } =
      getState().orderVerification.attempt;

    const payload = {
      validationType,
      phoneNumberId: phoneUUID,
      phoneValidationToken: validationToken
    };

    await verifyOrder(api, {
      type,
      token: validationToken,
      orderUUID,
      payload
    });
    return "success";
  } catch (error) {
    sendBugsnagError(error, "Modules/OrderVerification/thunk/resend");
    logging(`ResendError: ${error.message}`)
      .error(error)
      .method(resend)
      .file(__filename)
      .phoneHome();
  }
};
